import React, { useEffect, useMemo, useState } from 'react'
import { MessageCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useMessageApi } from '../hooks/use-message-api'
import { initializeI18n } from '../lib/i18n'
import { cn, mergeSx, sxToCSS, sxToStyleTag } from '../lib/utils'
import type { EntityChatTriggerProps } from '../types'
import EntityChatPanel from './entity-chat-panel'
import { useMessageComponents } from './message-components-provider'
import { Badge } from './ui/badge'
import { Button } from './ui/button'

// Module-level singleton: one matchMedia listener shared across all instances.
// Prevents 50+ resize listeners from flooding React with simultaneous state updates.
const LARGE_SCREEN_QUERY = '(min-width: 1025px)'
const _subscribers = new Set<(value: boolean) => void>()
let _isLargeScreen =
  typeof window !== 'undefined'
    ? window.matchMedia(LARGE_SCREEN_QUERY).matches
    : true
if (typeof window !== 'undefined') {
  window
    .matchMedia(LARGE_SCREEN_QUERY)
    .addEventListener('change', (e: MediaQueryListEvent) => {
      _isLargeScreen = e.matches
      _subscribers.forEach((cb) => cb(e.matches))
    })
}

function useSharedIsLargeScreen(): boolean {
  const [value, setValue] = useState(_isLargeScreen)
  useEffect(() => {
    setValue(_isLargeScreen)
    _subscribers.add(setValue)
    return () => {
      _subscribers.delete(setValue)
    }
  }, [])
  return value
}

export default function EntityChatTrigger({
  entityTypeId,
  entityId,
  variant = 'icon',
  size = 'md',
  position = 'bottom-right', // For floating variant
  panelPosition = 'right',
  slideOrigin = 'right',
  animationDuration = 300,
  className,
  sx,
  language = 'en',
  customTranslations,
  title,
  internalOnly = false,
  showRelatedEntities = true,
  autoIncludeRelatedTypes = [],
}: EntityChatTriggerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [messageCount, setMessageCount] = useState(0)
  const [mounted, setMounted] = useState(false)
  const isLargeScreen = useSharedIsLargeScreen()
  const [isAnimating, setIsAnimating] = useState(false)
  const [shouldSlideIn, setShouldSlideIn] = useState(false)

  // Panel resizing state - only for large screens
  const [panelSize, setPanelSize] = useState(() => ({
    width: '40vw',
    height: '50vh',
  }))
  const [isResizing, setIsResizing] = useState(false)
  const resizeStartRef = React.useRef<{
    startX: number
    startY: number
    startWidth: number
    startHeight: number
  } | null>(null)
  const panelRef = React.useRef<HTMLDivElement>(null)

  const { config } = useMessageComponents()
  const { userToken, sx: providerSx } = config
  // Initialize i18n synchronously before any hooks to ensure consistent useTranslation behavior
  initializeI18n(language, customTranslations)
  const { t } = useTranslation()
  // Merge provider sx with component sx (component sx has higher priority)
  const mergedSx = useMemo(() => mergeSx(providerSx, sx), [providerSx, sx])

  // Get API service instance
  const apiService = useMessageApi()
  // helper
  const slugifyClassName = (v: string) =>
    v
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')

  // Get positioning styles for floating variant
  const getPositionStyles = (): React.CSSProperties => {
    switch (position) {
      case 'bottom-left':
        return { bottom: '16px', left: '16px' }
      case 'top-right':
        return { top: '16px', right: '16px' }
      case 'top-left':
        return { top: '16px', left: '16px' }
      case 'bottom-right':
      default:
        return { bottom: '16px', right: '16px' }
    }
  }

  // Panel slide positioning logic
  const getSlideClasses = () => {
    const baseClasses = 'entity-chat-panel-base'

    switch (panelPosition) {
      case 'left':
        return `${baseClasses} entity-chat-panel-position-left`
      case 'right':
        return `${baseClasses} entity-chat-panel-position-right`
      case 'top':
        return `${baseClasses} entity-chat-panel-position-top`
      case 'bottom':
        return `${baseClasses} entity-chat-panel-position-bottom`
      default:
        return `${baseClasses} entity-chat-panel-position-right`
    }
  }

  const getSlideTransform = (
    shouldSlideIn: boolean,
    slideOrigin: 'left' | 'right' | 'bottom' | 'top',
    panelPosition: 'left' | 'right' | 'bottom' | 'top'
  ) => {
    if (shouldSlideIn) return 'translate(0, 0)'

    const transforms: Record<
      'left' | 'right' | 'bottom' | 'top',
      Partial<Record<'left' | 'right' | 'bottom' | 'top', string>> & {
        default: string
      }
    > = {
      left: {
        right: 'translateX(-100vw)',
        default: 'translateX(-100%)',
      },
      right: {
        left: 'translateX(100vw)',
        default: 'translateX(100%)',
      },
      bottom: {
        top: 'translateY(100vh)',
        default: 'translateY(100%)',
      },
      top: {
        bottom: 'translateY(-100vh)',
        default: 'translateY(-100%)',
      },
    }

    const originTransforms = transforms[slideOrigin]
    if (!originTransforms) return 'translate(0, 0)'

    return originTransforms[panelPosition] ?? originTransforms.default
  }

  // Function to handle message count updates from the panel
  const handleMessageCountChanged = (count: number): void => {
    setMessageCount(count)
  }

  // Resize handlers for panel
  const handleResizeStart = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)

    const rect = panelRef.current?.getBoundingClientRect()
    if (!rect) return
    resizeStartRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startWidth: rect.width,
      startHeight: rect.height,
    }
  }, [])

  const handleResizeMove = React.useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !resizeStartRef.current) return

      const { startX, startY, startWidth, startHeight } = resizeStartRef.current
      const deltaX = e.clientX - startX
      const deltaY = e.clientY - startY

      let newWidth = startWidth
      let newHeight = startHeight

      // Calculate new dimensions based on panel position
      if (panelPosition === 'right') {
        newWidth = Math.max(
          300,
          Math.min(window.innerWidth * 0.8, startWidth - deltaX)
        )
      } else if (panelPosition === 'left') {
        newWidth = Math.max(
          300,
          Math.min(window.innerWidth * 0.8, startWidth + deltaX)
        )
      } else if (panelPosition === 'top') {
        newHeight = Math.max(
          300,
          Math.min(window.innerHeight * 0.8, startHeight + deltaY)
        )
      } else if (panelPosition === 'bottom') {
        newHeight = Math.max(
          300,
          Math.min(window.innerHeight * 0.8, startHeight - deltaY)
        )
      }

      setPanelSize({
        width: `${newWidth}px`,
        height: `${newHeight}px`,
      })
    },
    [isResizing, panelPosition]
  )

  const handleResizeEnd = React.useCallback(() => {
    setIsResizing(false)
    resizeStartRef.current = null
  }, [])

  // Fetch initial message count when component mounts or entity changes
  // This provides a fallback count before the panel is opened
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const count = await apiService.fetchMessageCountWithToken(
          entityTypeId,
          entityId
        )
        setMessageCount(count)
      } catch (error) {
        // Error is already logged in the service
      }
    }

    if (userToken) {
      fetchCount()
    }
  }, [entityTypeId, entityId, userToken, apiService])

  // Handle mounting for portal
  useEffect(() => {
    setMounted(true)
  }, [])

  // Reset panel size when screen size changes
  const prevIsLargeScreenRef = React.useRef(isLargeScreen)
  useEffect(() => {
    if (prevIsLargeScreenRef.current !== isLargeScreen) {
      setPanelSize({
        width: '40vw',
        height: '50vh',
      })
      prevIsLargeScreenRef.current = isLargeScreen
    }
  }, [isLargeScreen])

  // Handle animation state for smooth transitions
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true)
      // Small delay to ensure DOM is ready, then trigger slide-in
      const slideTimeout = setTimeout(() => {
        setShouldSlideIn(true)
      }, 10)
      return () => clearTimeout(slideTimeout)
    } else {
      // Start slide-out immediately
      setShouldSlideIn(false)
      // Keep animating for the duration of the transition
      const timeout = setTimeout(() => {
        setIsAnimating(false)
      }, animationDuration) // Match the CSS transition duration
      return () => clearTimeout(timeout)
    }
  }, [isOpen, animationDuration])

  // Lock body scroll when panel is open and add escape key handler
  useEffect(() => {
    if (!isOpen) return () => {}

    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    // Add escape key listener
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', handleEscape)

    return () => {
      document.body.style.overflow = prevOverflow
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  // Add global mouse event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove)
      document.addEventListener('mouseup', handleResizeEnd)
      document.body.style.cursor =
        panelPosition === 'top' || panelPosition === 'bottom'
          ? 'ns-resize'
          : 'ew-resize'
      document.body.style.userSelect = 'none'

      return () => {
        document.removeEventListener('mousemove', handleResizeMove)
        document.removeEventListener('mouseup', handleResizeEnd)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }
  }, [isResizing, handleResizeMove, handleResizeEnd, panelPosition])

  const sizeClasses = {
    sm: 'h-6 w-6',
    md: 'h-8 w-8',
    lg: 'h-10 w-10',
  }

  const getIconSize = (size: string): string => {
    const sizeMap = {
      sm: '12px',
      md: '20px',
      lg: '24px',
    }
    return sizeMap[size as keyof typeof sizeMap] || sizeMap.md
  }

  const getBadgeSize = (
    size: string
  ): { width: string; height: string; fontSize: string } => {
    const sizeMap = {
      sm: { width: '12px', height: '12px', fontSize: '8px' },
      md: { width: '16px', height: '16px', fontSize: '10px' },
      lg: { width: '20px', height: '20px', fontSize: '11px' },
    }
    return sizeMap[size as keyof typeof sizeMap] || sizeMap.md
  }

  // Show loading state or render with available data
  // Component can render before user is loaded to show initial count

  // Generate stable class for this instance
  const instanceClass = useMemo(
    () => `entity-chat-trigger-${slugifyClassName(String(entityTypeId))}-${slugifyClassName(String(entityId))}`,
    [entityTypeId, entityId]
  )

  // Panel overlay component - only render when mounted and panel should be shown
  const renderPanelOverlay = () => {
    if (!mounted || (!isOpen && !isAnimating)) return null

    return (
      <div className="entity-chat-panel-container">
        {/* Overlay - fades in/out */}
        <div
          className="entity-chat-panel-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: shouldSlideIn
              ? 'rgba(0, 0, 0, 0.5)'
              : 'rgba(0, 0, 0, 0)',
            zIndex: 999,
            pointerEvents: isAnimating ? 'auto' : 'none',
            transition: `background-color ${animationDuration}ms ease-in-out`,
          }}
          role="button"
          tabIndex={0}
          aria-label={t('accessibility.closeChatPanel')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setIsOpen(false)
            }
          }}
          onClick={() => setIsOpen(false)}
          onWheel={(e) => e.preventDefault()}
          onTouchMove={(e) => e.preventDefault()}
        />

        {/* Sliding Chat Panel Container */}
        <div
          ref={panelRef}
          className={cn(
            getSlideClasses(),
            'entity-chat-panel-wrapper',
            instanceClass
          )}
          style={{
            position: 'fixed',
            ...(panelPosition === 'top' || panelPosition === 'bottom'
              ? {
                  height: isLargeScreen ? panelSize.height : '50vh',
                  minHeight: '300px',
                  maxHeight: '80vh',
                  width: '100vw',
                }
              : {
                  height: 'calc(100vh - var(--entity-chat-top-offset, 0px))',
                  width: isLargeScreen ? panelSize.width : '100vw',
                  minWidth: isLargeScreen ? '300px' : '100vw',
                  maxWidth: isLargeScreen ? '80vw' : '100vw',
                }),
            zIndex: 1000,
            transform: getSlideTransform(
              shouldSlideIn,
              slideOrigin,
              panelPosition
            ),
            transition: `transform ${animationDuration}ms ease-in-out`,
          }}
        >
          <EntityChatPanel
            entityTypeId={entityTypeId}
            entityId={entityId}
            onClose={() => setIsOpen(false)}
            className={instanceClass}
            language={language}
            customTranslations={customTranslations}
            onMessageCountChanged={handleMessageCountChanged}
            title={title}
            showCloseButton={true}
            internalOnly={internalOnly}
            showRelatedEntities={showRelatedEntities}
            autoIncludeRelatedTypes={autoIncludeRelatedTypes}
          />

          {/* Resize Handle */}
          {isLargeScreen && (
            <div
              className={cn(
                'entity-chat-panel-resize-handle',
                panelPosition === 'right' &&
                  'entity-chat-panel-resize-handle-left',
                panelPosition === 'left' &&
                  'entity-chat-panel-resize-handle-right',
                panelPosition === 'top' &&
                  'entity-chat-panel-resize-handle-bottom',
                panelPosition === 'bottom' &&
                  'entity-chat-panel-resize-handle-top'
              )}
              onMouseDown={handleResizeStart}
            />
          )}
        </div>
      </div>
    )
  }

  if (variant === 'floating') {
    return (
      <div className={cn('entity-chat-container', instanceClass)}>
        {/* Generate CSS for nested selectors - covers both trigger and panel */}
        {mergedSx && <style>{sxToStyleTag(mergedSx, instanceClass)}</style>}
        {/* Chat Toggle Button */}
        <div
          className="entity-chat-floating-button-container"
          style={getPositionStyles()}
        >
          <Button
            onClick={() => setIsOpen(true)}
            className="entity-chat-floating-button"
            variant="ghost"
            size="icon"
            aria-label={t('accessibility.openChat')}
          >
            <MessageCircle className="entity-chat-floating-icon" />
            {messageCount > 0 && (
              <Badge
                variant="destructive"
                className="entity-chat-floating-badge"
              >
                {messageCount > 9 ? '9+' : messageCount}
              </Badge>
            )}
          </Button>
        </div>

        {/* Panel Overlay - rendered inline */}
        {renderPanelOverlay()}
      </div>
    )
  }

  if (variant === 'button') {
    return (
      <div
        className={cn(
          'entity-chat-trigger-container entity-chat-trigger-button-container',
          instanceClass
        )}
        style={sxToCSS(mergedSx)}
      >
        {/* Generate CSS for nested selectors - covers both trigger and panel */}
        {mergedSx && <style>{sxToStyleTag(mergedSx, instanceClass)}</style>}
        <Button
          onClick={() => setIsOpen(true)}
          variant="outline"
          size="sm"
          className={cn('entity-chat-trigger-button', className)}
          aria-label={t('accessibility.openChat')}
        >
          <MessageCircle
            className="entity-chat-trigger-icon"
            style={{
              width: getIconSize(size),
              height: getIconSize(size),
            }}
          />
          {messageCount > 0 && (
            <Badge
              variant="secondary"
              className="entity-chat-trigger-badge entity-chat-trigger-badge-button"
            >
              {messageCount > 99 ? '99+' : messageCount}
            </Badge>
          )}
        </Button>

        {/* Panel Overlay - rendered inline */}
        {renderPanelOverlay()}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'entity-chat-trigger-container entity-chat-trigger-icon-container',
        instanceClass
      )}
      style={sxToCSS(mergedSx)}
    >
      {/* Generate CSS for nested selectors - covers both trigger and panel */}
      {mergedSx && <style>{sxToStyleTag(mergedSx, instanceClass)}</style>}
      <Button
        onClick={() => setIsOpen(true)}
        variant="ghost"
        size="icon"
        className={cn(
          'entity-chat-trigger-icon-button',
          sizeClasses[size as keyof typeof sizeClasses],
          className
        )}
        aria-label={t('accessibility.openChat')}
      >
        <MessageCircle
          className="entity-chat-trigger-icon"
          style={{
            width: getIconSize(size),
            height: getIconSize(size),
          }}
        />
        {messageCount > 0 && (
          <Badge
            variant="destructive"
            className="entity-chat-trigger-badge entity-chat-trigger-badge-icon"
            style={{
              top: '0px',
              right: '0px',
              ...getBadgeSize(size),
            }}
          >
            {messageCount > 99 ? '99+' : messageCount}
          </Badge>
        )}
      </Button>

      {/* Panel Overlay - rendered inline */}
      {renderPanelOverlay()}
    </div>
  )
}
